import React, { useState } from "react";
import { Download, FileText, BarChart3 } from "lucide-react";
import client from "@/lib/api";

// "Download progress report": the same panel the Little Verses (Poems) school pages have. Pick Overall / Daily / Weekly /
// Monthly, then download a PDF or an Excel file. `path` + `params` say whose report it is (a school, or one teacher's class).
const SCOPES = [
  ["overall", "Overall"],
  ["daily", "Daily"],
  ["weekly", "Weekly"],
  ["monthly", "Monthly"],
];

async function readError(err) {
  const data = err?.response?.data;
  if (data instanceof Blob) {   // a JSON error body also arrives as a Blob when responseType is "blob"
    try { const j = JSON.parse(await data.text()); if (typeof j?.detail === "string") return j.detail; } catch { /* not JSON */ }
  }
  return typeof data?.detail === "string" ? data.detail : "Could not build the report. Please try again.";
}

const slug = (t) => String(t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function ProgressReportPanel({ path, params = {}, fileBase = "", subtitle = "every student and every teacher in one document", studentsToggle = false }) {
  const [scope, setScope] = useState("overall");
  const [includeStudents, setIncludeStudents] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");

  const download = async (format) => {
    setBusy(format);
    setError("");
    try {
      const res = await client.get(path, { params: { ...params, period: scope, format, ...(studentsToggle && format === "pdf" ? { include_students: includeStudents } : {}) }, responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const name = (cd.match(/filename="?([^";]+)"?/) || [])[1] || `stories-progress${fileBase ? "_" + slug(fileBase) : ""}_${scope}.${format}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(await readError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="mt-10" data-testid="school-analytics-reports">
      <div className="flex items-center gap-2">
        <Download size={22} strokeWidth={2.6} className="text-[#2C5F3B]" />
        <h3 className="hv-heading text-xl text-[#2C5F3B]">Download progress report</h3>
      </div>
      <p className="mt-1 text-sm text-[#5F6B60]">Pick a report below, then download it as a PDF or an Excel file — {subtitle}.</p>
      <div className="mt-3 rounded-2xl bg-white border border-[#D5F2E0] p-5 space-y-4">
        <div className="flex flex-wrap gap-2" data-testid="school-report-scope">
          {SCOPES.map(([key, label]) => (
            <button key={key} type="button" onClick={() => setScope(key)} disabled={!!busy} data-testid={`school-report-scope-${key}`}
              className={`px-4 py-1.5 rounded-full text-sm font-heading font-semibold border-2 transition-colors disabled:opacity-50 ${scope === key ? "bg-[#2C5F3B] border-[#2C5F3B] text-white" : "bg-white border-[#D5F2E0] text-[#5F6B60] hover:border-[#4ECB7E]"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button type="button" onClick={() => download("pdf")} disabled={!!busy} data-testid="school-report-pdf"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2E9E4F] text-white px-5 py-2.5 font-heading font-semibold text-sm hover:bg-[#247E3F] transition-colors disabled:opacity-50">
            <FileText size={16} strokeWidth={2.6} /> {busy === "pdf" ? "Preparing…" : "Download PDF"}
          </button>
          <button type="button" onClick={() => download("xlsx")} disabled={!!busy} data-testid="school-report-xlsx"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white border-2 border-[#D5F2E0] px-5 py-2.5 font-heading font-semibold text-[#2C5F3B] text-sm hover:border-[#4ECB7E] transition-colors disabled:opacity-50">
            <BarChart3 size={16} strokeWidth={2.6} /> {busy === "xlsx" ? "Preparing…" : "Download Excel"}
          </button>
        </div>
        {studentsToggle && (
          <label className="flex items-center justify-center gap-2 text-sm text-[#5F6B60]" data-testid="school-report-include-students">
            <input type="checkbox" checked={includeStudents} onChange={(e) => setIncludeStudents(e.target.checked)} className="accent-[#2C5F3B]" />
            Include every student in the PDF <span className="text-xs">(adds pages; the Excel file always has them)</span>
          </label>
        )}
        {error && <p className="text-sm text-center text-red-700" role="alert" data-testid="school-report-error">{error}</p>}
        <p className="text-xs text-[#5F6B60] text-center">
          Stories opened, completion and status for each student{scope === "overall" ? ", as things stand right now." : ` — plus who was active in the ${scope} window.`}
        </p>
      </div>
    </section>
  );
}
